import React, { createContext, useContext, useState, ReactNode } from 'react';
import { OrderItem } from '../models/Order';

interface CartContextType {
  cartItems: OrderItem[];
  addToCart: (item: OrderItem) => void;
  removeFromCart: (cartIndex: number) => void;
  updateQuantity: (cartIndex: number, quantity: number) => void;
  clearCart: () => void;
  getTotalAmount: () => number;
  getItemCount: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cartItems, setCartItems] = useState<OrderItem[]>([]);

  const addToCart = (item: OrderItem) => {
    setCartItems(prev => {
      // Helper function to check if two items are identical (including add-ons and special instructions)
      const isSameItem = (a: OrderItem, b: OrderItem): boolean => {
        // Check basic properties
        if (a.itemId !== b.itemId || a.variationId !== b.variationId) {
          return false;
        }

        // Check special instructions
        if (a.specialInstructions !== b.specialInstructions) {
          return false;
        }

        // Check add-ons (must have same add-ons)
        const aAddOns = a.addOns || [];
        const bAddOns = b.addOns || [];

        if (aAddOns.length !== bAddOns.length) {
          return false;
        }

        // Sort and compare add-on IDs
        const aAddOnIds = aAddOns.map(ao => ao.id).sort();
        const bAddOnIds = bAddOns.map(ao => ao.id).sort();

        return aAddOnIds.every((id, index) => id === bAddOnIds[index]);
      };

      // Check if identical item already exists
      const existingIndex = prev.findIndex(i => isSameItem(i, item));

      if (existingIndex >= 0) {
        // Update quantity of existing item
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + item.quantity
        };
        return updated;
      }

      // Add new item
      return [...prev, item];
    });
  };

  const removeFromCart = (cartIndex: number) => {
    setCartItems(prev => prev.filter((_, index) => index !== cartIndex));
  };

  const updateQuantity = (cartIndex: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(cartIndex);
      return;
    }

    setCartItems(prev =>
      prev.map((item, index) =>
        index === cartIndex ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getTotalAmount = () => {
    return cartItems.reduce((total, item) => {
      const itemTotal = item.price * item.quantity;
      const addOnsTotal = item.addOns?.reduce((sum, addOn) => sum + addOn.price, 0) || 0;
      return total + itemTotal + (addOnsTotal * item.quantity);
    }, 0);
  };

  const getItemCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalAmount,
        getItemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
